import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { SHA256 as sha256 } from "crypto-js";
import _ from "lodash";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ts 3.6x
export function debounce<T extends Function>(cb: T, wait = 500) {
  let h: any = 0;
  let callable = (...args: any) => {
    clearTimeout(h);
    h = setTimeout(() => cb(...args), wait);
  };
  return <T>(<any>callable);
}

export function coalesce(arr: any[] | string | undefined, def: any = null) {
  if (!arr || arr.length == 0) return def;
  return arr;
}

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export function textToColor(text: string) {
  // Create a hash of the input text
  const hash = sha256(text).toString();

  // Convert the first six characters of the hash to RGB values
  const r = parseInt(hash.substring(0, 2), 16);
  const g = parseInt(hash.substring(2, 4), 16);
  const b = parseInt(hash.substring(4, 6), 16);

  // Return the RGB color as a hex string
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export const getCityColor = (city: string): string => {
  // 1. Robust and deterministic hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < city.length; i++) {
    const char = city.charCodeAt(i);
    hash = (hash << 5) + hash + char; /* hash * 33 + char */
    hash |= 0; // Convert to 32bit integer to ensure consistent behavior
  }
  // Work with a positive hash for easier modulo operations later
  const positiveHash = Math.abs(hash);

  // 2. Generate HSL values from the hash
  // Hue: Use the golden angle (approx 137.5 degrees) for better distribution
  // This tends to produce more visually distinct hues when mapped to a circle.
  const hue = (positiveHash * 137.508) % 360;

  // Saturation: Keep it reasonably vibrant for distinctiveness.
  // Range: 50% to 80% (a 30 point range)
  // We use (positiveHash % 31) which gives a value from 0 to 30.
  const saturation = 50 + (positiveHash % 31);

  // Lightness: Keep it high for readability with dark text.
  // Range: 80% to 92% (a 12 point range, ensuring it's always very light)
  // We use a different manipulation of the hash to vary lightness a bit.
  // Integer division changes the number, then modulo.
  const lightness = 80 + (Math.floor(positiveHash / 31) % 13);

  // 3. Convert HSL to HEX
  // Standard HSL to RGB conversion formula
  const s = saturation / 100;
  const l = lightness / 100;

  // Chromaticity (c)
  const c = (1 - Math.abs(2 * l - 1)) * s;
  // Intermediate value for calculating R, G, B
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  // Amount to add to each component to match lightness
  const m = l - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (hue >= 0 && hue < 60) {
    rPrime = c;
    gPrime = x;
    bPrime = 0;
  } else if (hue >= 60 && hue < 120) {
    rPrime = x;
    gPrime = c;
    bPrime = 0;
  } else if (hue >= 120 && hue < 180) {
    rPrime = 0;
    gPrime = c;
    bPrime = x;
  } else if (hue >= 180 && hue < 240) {
    rPrime = 0;
    gPrime = x;
    bPrime = c;
  } else if (hue >= 240 && hue < 300) {
    rPrime = x;
    gPrime = 0;
    bPrime = c;
  } else if (hue >= 300 && hue < 360) {
    rPrime = c;
    gPrime = 0;
    bPrime = x;
  }

  const toHex = (val: number): string => {
    const hex = Math.round(val * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  const r = toHex(rPrime + m);
  const g = toHex(gPrime + m);
  const b = toHex(bPrime + m);

  const hexColor = `#${r}${g}${b}`;
  // 4. Return as a Tailwind arbitrary background color
  return hexColor;
};

export function isFormDirty(
  dirtyFields: Record<string, unknown>,
  currentValues: Record<string, unknown>,
  defaultValues: Record<string, unknown> | undefined | null,
): boolean {
  if (!defaultValues) return true;
  return Object.keys(dirtyFields).some(
    (key) => !_.isEqual(currentValues[key], defaultValues[key]),
  );
}

export function getDirtyValues<T extends Record<string, unknown>>(
  dirtyFields: Record<string, unknown>,
  values: T,
): Partial<T> {
  return getDirtyPaths(dirtyFields).reduce(
    (acc, key) => {
      if (dirtyFields[key]) {
        (acc as Record<string, unknown>)[key] = values[key];
      }
      return acc;
    },
    {} as Partial<T>,
  );
}

export function getDirtyPaths(obj: Record<string, any>): string[] {
  function hasTrue(value: any): boolean {
    if (value === true) return true;
    if (Array.isArray(value)) return value.some(hasTrue);
    if (typeof value === "object" && value !== null)
      return Object.values(value).some(hasTrue);
    return false;
  }

  return Object.entries(obj)
    .filter(([, value]) => hasTrue(value))
    .map(([key]) => key);
}

export function getChangedValues<T extends Record<string, unknown>>(
  currentValues: T,
  originalValues: T | null | undefined,
): Partial<T> {
  if (!originalValues) return currentValues;
  return Object.keys(currentValues).reduce(
    (acc, key) => {
      if (
        !_.isEqual(
          currentValues[key],
          (originalValues as Record<string, unknown>)[key],
        )
      ) {
        (acc as Record<string, unknown>)[key] = currentValues[key];
      }
      return acc;
    },
    {} as Partial<T>,
  );
}

/**
 * Re-adds the fields an operator deliberately CLEARED, which AutoForm drops.
 *
 * WHY THIS EXISTS (do not "simplify" it away): `AutoForm.handleSubmit`
 * (`@autoform/react`) pipes the submitted values through `removeEmptyValues()`
 * (`@autoform/core`) BEFORE validating and before calling our `onSubmit`. That
 * helper *deletes* every key whose value is `null`, `undefined` or `""`:
 *
 *   if ([null, undefined, "", [], {}].includes(value)) continue;
 *
 * So a text input the curator emptied does not reach `onSubmit` as `""` — it
 * does not reach it at all. Our payload builders (`getChangedValues` /
 * `getDirtyValues`) therefore have nothing to diff, the PUT body carries no
 * such field, and the API reads "absent" as "not provided" and leaves the
 * stored value untouched: a silent no-op that still reports success.
 *
 * We cannot drop `removeEmptyValues` — it is what lets empty optional fields
 * (e.g. `z.string().url().nullish()`) pass validation — so we recover the
 * intent from the RAW react-hook-form values instead: a key whose ORIGINAL
 * value was a non-empty string and whose raw value is now `""` was cleared on
 * purpose, so we send it explicitly.
 *
 * We send `""`, never `null`: the API's `FirestoreUpdateBuilder.Set()` skips
 * `null` by design (it means "not provided — leave the stored value alone"),
 * while any other value is written through. So `""` is the only way to say
 * "clear this", and the field ends up STORED as `""` — nothing is deleted from
 * the document (deleting would strand the old value in Knock, which can only
 * unset a property via an explicit `null`, and would break consumers that
 * expect a non-null string).
 *
 * `clearableKeys` is a deliberate allowlist, not a heuristic: `""` is only safe
 * for fields the API actually accepts it for. Several endpoints treat `""` as
 * "present" and then fail their own format rules (`UpdateMovieCommandValidator`
 * regex-checks `Slug`/`OriginalLanguage` `.When(… is not null)` → 500;
 * `UpdateGeodataCommandValidator` has `Timezone.NotEmpty()` → 400), and enum
 * properties (`VenueStatusDb?`, `ArtistStatusDb?`) reject `""` in
 * System.Text.Json binding → 400 for the whole request. So a key belongs in an
 * allowlist only once someone has checked the endpoint accepts `""` for it.
 *
 * Top-level scalars only, on purpose. Nested objects already round-trip:
 * `removeEmptyValues` recurses, but the parent object still differs from its
 * default, so the parent lands in the diff with the inner key absent and the
 * API DTO binds that as null.
 *
 * Only `""` counts as cleared — never `null`, and never a missing key.
 * `SelectField` dispatches `null` when a curator picks "Select an option"
 * (`src/components/ui/autoform/components/SelectField.tsx`), and `DateTimeField`
 * leaves cleared dates as `undefined`; treating either as a clear would send
 * `""` for an enum or a date and 400 the entire request.
 */
export function withClearedFields<T extends Record<string, unknown>>(
  changed: Partial<T>,
  rawValues: Record<string, unknown>,
  defaultValues: Record<string, unknown> | null | undefined,
  clearableKeys: Iterable<string>,
): Partial<T> {
  const result = { ...changed } as Record<string, unknown>;
  if (!defaultValues) return result as Partial<T>;

  for (const key of new Set(clearableKeys)) {
    // Already carrying a value? Leave it alone. A key present but `undefined`
    // counts as absent — `JSON.stringify` would drop it from the payload.
    if (result[key] !== undefined) continue;

    // Only a non-empty string can be "cleared". Numbers/booleans/arrays/objects
    // and originally-empty fields are left untouched.
    if (!Object.hasOwn(defaultValues, key)) continue;
    const original = defaultValues[key];
    if (typeof original !== "string" || original === "") continue;

    // Strictly `""`. An unregistered field is `undefined` here and is
    // indistinguishable from a cleared one, so it never counts.
    if (rawValues[key] === "") result[key] = "";
  }
  return result as Partial<T>;
}

export function tryParseJson(str: string | null): any {
  try {
    if (!str) {
      return {};
    }
    return JSON.parse(str);
  } catch (e) {
    return {};
  }
}
